import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import {
  BoldIcon,
  CapitalizeIcon,
  CaretDownIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListNumbersIcon,
  RotateCcwIcon,
  RotateCwIcon,
} from '@persona-id/design-tokens/icons';
import Link from '@tiptap/extension-link';
import { EditorView } from '@tiptap/pm/view';
import { EditorContent, Extensions, useEditor } from '@tiptap/react';
import { Editor } from '@tiptap/react/dist/packages/react/src/Editor';
import StarterKit from '@tiptap/starter-kit';
import dompurify from 'dompurify';
import { noop } from 'lodash';
import { KeyboardEventHandler, useEffect, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';
import { Markdown } from 'tiptap-markdown';

import EditorGlobalCss from 'dashboard/components-lib/RichTextEditor/styles';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { TextField } from 'design-system/components/FormField';
import Popover from 'design-system/components/Popover';
import { HStack } from 'design-system/components/Stack';
import { Elevation } from 'design-system/variables/effect';
import useToggle from 'lib/hooks/useToggle';
import { intersperse } from 'lib/utils/array';

const Bar = styled('div')`
  display: flex;
  height: fit-content;
  padding: ${Spacing[1]} ${Spacing[1]};
  gap: 2px;
  border-bottom: 1px solid ${Color.BorderHairline};
`;

const TextEditor = styled('div')`
  background-clip: padding-box;
  border-radius: ${Spacing[1]};
  border: 1px solid ${Color.CoreSlate300};
`;

const MenuBarItem = styled.button<{ isActive: boolean }>`
  background: ${Color.PrimaryWhite};
  display: flex;
  align-items: center;
  padding: ${Spacing[1]};
  cursor: pointer;
  border-radius: ${BorderRadius.Md};

  ${({ isActive }) => isActive && ` background-color: ${Color.CoreSlate100};`};

  svg {
    margin: 0;
  }

  &:hover {
    box-shadow: ${Elevation.Low};
  }
`;

const StyledCard = styled(Card)`
  min-width: 300px;
  padding: ${Spacing[3]};
`;

const VerticalLine = styled.span`
  border-right: 1px solid ${Color.CoreSlate300};
`;

// TODO (Amina) : Size configurable dropdown items
const StyledDropdownItem = styled(DropdownMenu.Item)`
  width: 100%;
  height: 30px;
  font-size: small;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

type MenuBarSection = 'undo-redo' | 'text-format' | 'text-style' | 'lists';
interface MenuBarInterface {
  editor: Editor | null;
  supportedSections?: Array<MenuBarSection>;
}
const MenuBar = ({
  editor,
  supportedSections = ['undo-redo', 'text-format', 'text-style', 'lists'],
}: MenuBarInterface) => {
  const [isEditLinkModal, { enable: showEditLinkModal, disable: hideEditLinkModal }] = useToggle();
  const [linkUrl, setLinkUrl] = useState('');

  if (!editor || supportedSections.length === 0) {
    return null;
  }

  const { view, state } = editor;
  const { from, to } = view.state.selection;
  const selectionText = state.doc.textBetween(from, to, '');

  const handleSetLink = (url: string) => {
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    hideEditLinkModal();
  };

  const handleLinkModalKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSetLink(linkUrl);
    }
  };

  const textFormatItems = () => {
    return [
      {
        id: 'MenuBar__paragraph',
        label: 'Normal text',
        variant: 'basic' as const,
        type: 'button',
        onClick: () => editor.chain().focus().setParagraph().run(),
      },
      {
        id: 'MenuBar__h1',
        label: 'Heading 1',
        variant: 'basic' as const,
        type: 'button',
        onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      },
      {
        id: 'MenuBar__h2',
        label: 'Heading 2',
        variant: 'basic' as const,
        type: 'button',
        onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      },
      {
        id: 'MenuBar__h3',
        label: 'Heading 3',
        variant: 'basic' as const,
        type: 'button',
        onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      },
    ];
  };

  const sectionsMap: Record<MenuBarSection, React.ReactNode> = {
    // undo + redo controls
    ['undo-redo']: (
      <>
        <MenuBarItem
          id='MenuBar__undo'
          type='button'
          aria-label='Undo'
          isActive={false}
          disabled={!editor.can().chain().focus().undo().run()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <RotateCcwIcon />
        </MenuBarItem>

        <MenuBarItem
          id='MenuBar__redo'
          type='button'
          aria-label='Redo'
          isActive={false}
          disabled={!editor.can().chain().focus().redo().run()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <RotateCwIcon />
        </MenuBarItem>
      </>
    ),
    // preset text sizes (normal, headings 1-3)
    ['text-format']: (
      <DropdownMenu
        trigger={
          <MenuBarItem
            id='MenuBar__textFormats'
            type='button'
            aria-label='Text sizes'
            isActive={false}
          >
            <>
              <CapitalizeIcon />
              <CaretDownIcon />
            </>
          </MenuBarItem>
        }
      >
        {textFormatItems().map((item) => (
          <StyledDropdownItem
            key={item.id}
            id={item.id}
            variant={item.variant}
            onClick={item.onClick}
          >
            {item.label}
          </StyledDropdownItem>
        ))}
      </DropdownMenu>
    ),
    // bold, italic, link
    ['text-style']: (
      <>
        <MenuBarItem
          id='MenuBar__bold'
          type='button'
          aria-label='Bold'
          isActive={editor.isActive('bold')}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldIcon />
        </MenuBarItem>

        <MenuBarItem
          id='MenuBar__italic'
          type='button'
          aria-label='Italic'
          isActive={editor.isActive('italic')}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <ItalicIcon />
        </MenuBarItem>

        <Popover
          open={isEditLinkModal}
          trigger={
            <MenuBarItem
              id='MenuBar__link'
              type='button'
              aria-label='Link'
              // disable link button if there is no selection and cursor is not on an existing link
              disabled={!editor.isActive('link') && selectionText === ''}
              isActive={editor.isActive('link')}
              onClick={() => {
                showEditLinkModal();
                setLinkUrl(editor.getAttributes('link').href || '');
              }}
            >
              <LinkIcon />
            </MenuBarItem>
          }
        >
          <Popover.Content side='bottom' align='center' style={{ zIndex: 100000 }}>
            <StyledCard elevated>
              <OutsideClickHandler onOutsideClick={hideEditLinkModal}>
                <TextField
                  size='sm'
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={handleLinkModalKeyDown}
                ></TextField>

                <HStack equal={true}>
                  <Button
                    id='Menubar__editLinkModal_cancel'
                    size='sm'
                    variant='basic'
                    onClick={hideEditLinkModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    id='Menubar__editLinkModal__save'
                    type='submit'
                    size='sm'
                    variant='primary'
                    onClick={() => {
                      handleSetLink(linkUrl);
                      hideEditLinkModal();
                    }}
                  >
                    Save
                  </Button>
                </HStack>
              </OutsideClickHandler>
            </StyledCard>
          </Popover.Content>
        </Popover>
      </>
    ),
    // bullet list, ordered list
    ['lists']: (
      <>
        <MenuBarItem
          id='MenuBar__bulletList'
          type='button'
          aria-label='Bullet list'
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListIcon />
        </MenuBarItem>

        <MenuBarItem
          id='MenuBar__orderedList'
          type='button'
          aria-label='Ordered list'
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListNumbersIcon />
        </MenuBarItem>
      </>
    ),
  };

  return (
    <Bar title='Menu bar'>
      {intersperse(
        supportedSections.map((section) => sectionsMap[section]),
        (i) => (
          <VerticalLine key={i} />
        ),
      )}
    </Bar>
  );
};

interface RichTextEditorInterface {
  className?: string;
  defaultValue?: string;
  menuBarSections?: Array<MenuBarSection>;
  additionalEditorExtensions?: Extensions;
  returnMarkdown?: boolean;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  onKeyDown?: (event?: KeyboardEvent) => void;
}

const Text = styled.div`
  cursor: default;
`;

const RichTextEditor = ({
  className,
  defaultValue,
  menuBarSections,
  additionalEditorExtensions = [],
  returnMarkdown = false,
  readOnly = false,
  onChange = noop,
  onKeyDown = noop,
}: RichTextEditorInterface) => {
  const editor = useEditor({
    extensions: [
      Markdown.configure({
        html: true,
        tightLists: true,
        tightListClass: 'tight',
        bulletListMarker: '-',
        linkify: false,
        breaks: false,
        transformPastedText: false,
        transformCopiedText: false,
      }),
      StarterKit,
      // We are setting the inclusive attribute to false so that text following the initial text serving as the url placeholder
      // is not considered part of the placeholder.
      Link.extend({ inclusive: false }).configure({
        // autolink is buggy alongside handlebars syntax, so we choose to disable it. Links can still be created using the menu bar item.
        autolink: false,
        openOnClick: false,
      }),
      ...additionalEditorExtensions,
    ],
    content: dompurify.sanitize(defaultValue),
    editorProps: {
      attributes: {
        title: 'Rich text editor',
      },
      handleKeyDown(_view: EditorView, event: KeyboardEvent) {
        onKeyDown(event);
      },
    },
    onUpdate: ({ editor }) => {
      const value = returnMarkdown ? editor.storage.markdown.getMarkdown() : editor.getHTML();
      // We need to return an empty string instead of 'value' because 'value' may be an empty set of html tags when
      // editor.isEmpty. By returning an empty string we have a consistent value to compare against.
      onChange(editor.isEmpty ? '' : dompurify.sanitize(value));
    },
  });

  useEffect(() => {
    if (editor) {
      const value = returnMarkdown ? editor.storage.markdown.getMarkdown() : editor.getHTML();
      if (defaultValue !== value) {
        const { from, to } = editor.state.selection;
        editor.commands.setContent(dompurify.sanitize(defaultValue), false, {
          preserveWhitespace: true,
        });
        editor.commands.setTextSelection({ from, to });
      }
    }
  }, [defaultValue, editor, returnMarkdown]);

  useEffect(() => {
    if (editor) {
      // second param prevents setEditable from emitting onUpdate
      // source: https://github.com/ueberdosis/tiptap/issues/4535#issuecomment-1767019649
      editor.setEditable(!readOnly, false);
    }
  }, [editor, readOnly]);

  if (!editor) {
    return null;
  }

  return (
    // We need the global styles here to overwrite our systems general styles which hinder the rendering of some of the rich text in the editor.
    <>
      <EditorGlobalCss />
      {readOnly ? (
        <Text className={className}>
          <EditorContent editor={editor} />
        </Text>
      ) : (
        <TextEditor className={className}>
          <MenuBar supportedSections={menuBarSections} editor={editor} />
          <EditorContent style={{ padding: Spacing[3] }} editor={editor} />
        </TextEditor>
      )}
    </>
  );
};
export default RichTextEditor;
